// src/components/common/Confirm/SixConfirmModalContainer.tsx
import React, { useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';

import { useAppDispatch } from '@/store';
import ConfirmModal from '@/components/molecules/ConfirmModal/ConfirmModal';
import { closeConfirm } from '@/store/slices/ui/uiSlice';
import { selectConfirm } from '@/store/selectors/ui';
import { deleteSixThunk } from '@/store/thunks/six/deleteSixThunk';
import { deleteFileThunk } from '@/store/thunks/sixDetail/deleteFileThunk';
import { deleteFolder } from '@/store/thunks/sixDetail/deleteFolderThunk';
import { fetchSixCatalog } from '@/store/thunks/sixCatalog/fetchSixCatalog';

interface ConfirmUi {
  title: string;
  paragraphs: string[];
  confirmText: string;
  onConfirm: () => Promise<void>;
}

export function SixConfirmModalContainer() {
  const dispatch = useAppDispatch();
  const confirm = useSelector(selectConfirm);

  const ui = useMemo<ConfirmUi | null>(() => {
    if (confirm === null) return null;

    // Handle Six deletion
    if (confirm.context === 'sixes' && confirm.kind === 'delete') {
      return {
        title: 'Delete SIX',
        paragraphs: [
          `Please confirm that you wish to delete ${confirm.row.name}.`,
          'This action cannot be undone. All files and activity associated with this SIX will be permanently deleted.',
        ],
        confirmText: 'Delete',
        onConfirm: async() => {
          await dispatch(deleteSixThunk(confirm.payload)).unwrap();
          await dispatch(fetchSixCatalog()).unwrap();
          dispatch(closeConfirm());
        },
      };
    }

    // Handle File deletion
    if (confirm.context === 'files' && confirm.kind === 'delete') {
      return {
        title: 'Delete File',
        paragraphs: [
          `Please confirm that you wish to delete "${confirm.row.name}".`,
          'This action cannot be undone.',
        ],
        confirmText: 'Delete',
        onConfirm: async() => {
          await dispatch(deleteFileThunk(confirm.payload)).unwrap();
          dispatch(closeConfirm());
        },
      };
    }

    // Handle Folder deletion
    if (confirm.context === 'folders' && confirm.kind === 'delete') {
      return {
        title: 'Delete Folder',
        paragraphs: [
          `Please confirm that you wish to delete the folder "${confirm.row.name}".`,
          'This will permanently delete the folder and all files and subfolders within it.',
          'This action cannot be undone.',
        ],
        confirmText: 'Delete Folder',
        onConfirm: async() => {
          await dispatch(deleteFolder({
            sixId: confirm.payload.sixId,
            folderPath: confirm.payload.folderPath,
          })).unwrap();
          dispatch(closeConfirm());
        },
      };
    }

    return null;
  }, [confirm, dispatch]);

  const handleClose = useCallback(() => {
    dispatch(closeConfirm());
  }, [dispatch]);

  if (ui === null) return null;

  return (
    <ConfirmModal
      open
      title={ui.title}
      paragraphs={ui.paragraphs}
      confirmText={ui.confirmText}
      onClose={handleClose}
      onConfirm={() => {
        void ui.onConfirm();
      }}
    />
  );
}
